// Product Compare Cards - Tab Switching
class ProductCompareCards extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    const section = this.closest('.s-product-compare-cards');
    if (!section) return;

    const buttons = Array.from(section.querySelectorAll('.compare-cards__selector-btn'));
    const cards = section.querySelectorAll('.compare-cards__card');

    const switchTo = (index) => {
      if (index < 0 || index >= buttons.length) return;
      const button = buttons[index];
      const productId = button.dataset.productId;

      buttons.forEach(btn => {
        btn.classList.remove('active');
        btn.setAttribute('aria-selected', 'false');
      });
      button.classList.add('active');
      button.setAttribute('aria-selected', 'true');

      cards.forEach(card => card.classList.remove('active'));
      const activeCard = section.querySelector(`.compare-cards__card[data-product-index="${productId}"]`);
      if (activeCard) activeCard.classList.add('active');

      button.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

      document.dispatchEvent(new CustomEvent('product-compare-cards:switch', {
        detail: { productIndex: productId }
      }));
    };

    buttons.forEach((button, i) => {
      button.addEventListener('click', () => switchTo(i));
    });

    this.initModals(section);
    this.observeProductNames(section);
    this.syncAll(section);

    window.addEventListener('load', () => this.syncAll(section));

    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => this.syncAll(section), 150);
    });

    this.watchHeaderHeight();
  }

  // Drives the sticky offset from its own live measurement on scroll/resize
  // instead of trusting the header's own --header-end update timing, since
  // that value can go stale (search collapse, editor iframe, etc.)
  watchHeaderHeight() {
    const header = document.querySelector('store-header');
    const nav = this.parentElement && this.parentElement.querySelector('.compare-cards__selector-nav');
    if (!header || !nav) return;

    let ticking = false;
    const updateOffset = () => {
      ticking = false;
      const headerEnd = Math.max(0, header.getBoundingClientRect().bottom);
      nav.style.top = `${headerEnd.toFixed(1)}px`;
    };
    const requestUpdate = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(updateOffset);
    };

    requestUpdate();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    if (window.ResizeObserver) {
      this.headerResizeObserver = new ResizeObserver(requestUpdate);
      this.headerResizeObserver.observe(header);
    }
  }

  initModals(section) {
    const openers = section.querySelectorAll('.js-open-modal');
    const modals = section.querySelectorAll('.media-grid-popup');
    if (!openers.length || !modals.length) return;

    const closeModal = (modal) => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      modal.removeEventListener('keydown', modal.escapeHandler);
      modal.opener?.setAttribute('aria-expanded', 'false');
      modal.opener?.focus();
    };

    openers.forEach((opener) => {
      opener.setAttribute('aria-expanded', 'false');
      opener.addEventListener('click', () => {
        const modal = section.querySelector(`#${CSS.escape(opener.dataset.modalId)}`);
        if (!modal) return;

        modal.opener = opener;
        modal.style.display = 'flex';
        document.body.style.overflow = 'hidden';
        opener.setAttribute('aria-expanded', 'true');
        modal.querySelector('[data-popup-close]')?.focus();

        modal.escapeHandler = (event) => {
          if (event.key === 'Escape') closeModal(modal);
        };
        modal.addEventListener('keydown', modal.escapeHandler);
      });
    });

    modals.forEach((modal) => {
      modal.querySelector('[data-popup-close]')?.addEventListener('click', () => closeModal(modal));
      modal.addEventListener('click', (event) => {
        if (event.target === modal) closeModal(modal);
      });
    });
  }

  syncAll(section) {
    if (window.innerWidth < 1025) return;
    // Step 1: equalize spec item heights within each row
    this.syncDesktopSpecRows(section);
    // Step 2: after reflow, equalize all row heights globally across every section
    requestAnimationFrame(() => scheduleSyncAllRowHeights());
  }

  // Observes each title's own image-wrap so it re-fits the instant it actually becomes visible/sized
  // (tab switch, resize, editor changes) instead of guessing with rAF/timeouts.
  observeProductNames(section) {
    const names = section.querySelectorAll('.compare-cards__product-name');
    if (!names.length) return;

    if (!('ResizeObserver' in window)) {
      names.forEach((name) => this.fitProductName(name));
      return;
    }

    if (!this.nameObserver) {
      this.nameObserver = new ResizeObserver((entries) => {
        entries.forEach((entry) => {
          const name = entry.target.querySelector('.compare-cards__product-name');
          if (name) this.fitProductName(name);
        });
      });
    }

    names.forEach((name) => {
      const container = name.closest('.compare-cards__image-wrap') || name.parentElement;
      if (container) this.nameObserver.observe(container);
    });
  }

  fitProductName(name) {
    const container = name.closest('.compare-cards__image-wrap') || name.parentElement;
    if (!container) return;

    const header = name.parentElement;
    const headerStyles = header ? getComputedStyle(header) : null;
    const horizontalPadding = headerStyles
      ? parseFloat(headerStyles.paddingLeft) + parseFloat(headerStyles.paddingRight)
      : 0;

    const availableWidth = container.clientWidth - horizontalPadding;
    if (!availableWidth) return; // not visible yet; ResizeObserver fires again once it is

    name.style.fontSize = '';
    name.style.whiteSpace = 'nowrap';
    name.style.overflow = 'visible';
    name.style.maxWidth = 'none';

    if (name.scrollWidth <= availableWidth) {
      name.style.maxWidth = '100%';
      return;
    }

    const baseSize = parseFloat(getComputedStyle(name).fontSize);
    const minimumSize = 12;
    let size = baseSize;
    while (name.scrollWidth > availableWidth && size > minimumSize) {
      size -= 0.5;
      name.style.fontSize = `${size}px`;
    }

    name.style.maxWidth = '100%';
  }

  syncDesktopSpecRows(section) {
    if (window.innerWidth < 1025) return;

    // Reset all min-heights first
    section.querySelectorAll('.compare-cards__desktop-spec-items .compare-cards__spec-item').forEach(el => {
      el.style.minHeight = '';
    });

    // Align spec items at the same index across section cards within this section
    section.querySelectorAll('.compare-cards__desktop-row').forEach(row => {
      const specGroups = Array.from(row.querySelectorAll('.compare-cards__desktop-spec-items'));
      if (specGroups.length < 2) return;

      const maxItems = Math.max(...specGroups.map(g => g.children.length));

      for (let i = 0; i < maxItems; i++) {
        const items = specGroups.map(g => g.children[i]).filter(Boolean);
        if (items.length < 2) continue;

        const maxHeight = Math.max(...items.map(el => el.getBoundingClientRect().height));
        items.forEach(el => el.style.minHeight = maxHeight + 'px');
      }
    });
  }
}

customElements.get('product-compare-cards') || customElements.define('product-compare-cards', ProductCompareCards);

let _syncRowTimer = null;
function scheduleSyncAllRowHeights() {
  clearTimeout(_syncRowTimer);
  _syncRowTimer = setTimeout(() => {
    if (window.innerWidth < 1025) return;
    const rows = Array.from(document.querySelectorAll('.compare-cards__desktop-row'));
    rows.forEach(el => { el.style.minHeight = ''; });
    const maxH = Math.max(...rows.map(el => el.getBoundingClientRect().height));
    if (maxH > 0) rows.forEach(el => { el.style.minHeight = maxH + 'px'; });
  }, 50);
}
